import React from "react";
import {
  Box,
  Chip,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";

import { useServices, useSnackbar } from "contexts";
import { getInterviewAttempts } from "services";
import { Session } from "utils";
import { useNavigateWithClient } from "hooks";

import Pagination from "../../components/Pagination";
import NullState from "../../components/NullState";
import AttemptCard, { AttemptCardSkeleton } from "./AttemptCard";
import { mapAttempts } from "./mapAttempts";

const PAGE_SIZE = 5;

// Ghost cards shown while attempts load — matches the desktop convention of
// rendering three skeletons (TemplateList).
const SKELETON_COUNT = 3;

// Matches the desktop AttemptsTable filter chips.
const FILTERS = [
  { field: "assignment", label: "Assignment" },
  { field: "practice", label: "Practice" },
];

/**
 * "Your attempted assessments" (mobile).
 *
 * Fetches all of the user's attempts for the batch the same way the desktop
 * AttemptsTable does (getInterviewAttempts with include interviewTemplate), then
 * applies search + Assignment/Practice filters and paginates client-side at 5
 * per page. The Figma scroll dots are replaced by the reusable <Pagination/>.
 */
export default function AttemptedAssessments({ batch, batchLoading = false }) {
  const service = useServices();
  const snackbar = useSnackbar();
  const navigateWithClient = useNavigateWithClient();

  const [allAttempts, setAllAttempts] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [activeFilter, setActiveFilter] = React.useState(null);
  const [page, setPage] = React.useState(1);

  React.useEffect(() => {
    if (!batch) return;

    (async () => {
      try {
        setLoading(true);
        const { interviewAttempts } = await service.callService(
          getInterviewAttempts,
          {
            user: Session.userId,
            batch,
            finishedAt: null,
            include: ["interviewTemplate", "user"],
            orderBy: "createdAt",
            order: "desc",
          }
        );
        setAllAttempts(mapAttempts(interviewAttempts || []));
      } catch (error) {
        console.error(error);
        snackbar.error("Unable to load attempts");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batch]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return allAttempts.filter((a) => {
      const matchesSearch = !q || (a.title || "").toLowerCase().includes(q);
      const matchesFilter = !activeFilter || a.type === activeFilter;
      return matchesSearch && matchesFilter;
    });
  }, [allAttempts, search, activeFilter]);

  const pageCount = Math.ceil(filtered.length / PAGE_SIZE) || 1;

  // Keep the current page valid when a batch switch, filter or search shrinks
  // the list.
  React.useEffect(() => {
    setPage(1);
  }, [search, activeFilter, batch]);

  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // The attempts fetch can't start until the batch resolves, so cover the batch
  // load too — otherwise the list flashes "No assessments found." first.
  const showSkeletons = batchLoading || loading;

  const toggleFilter = (field) =>
    setActiveFilter((current) => (current === field ? null : field));

  return (
    <Box
      sx={{
        bgcolor: "shades.clr-white-900",
        border: "1px solid",
        borderColor: "neutral.clr-100",
        borderRadius: 3,
        p: 4,
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <Box>
        <Typography variant="h6-semiBold" color="neutral.clr-800">
          Your attempted assessments
        </Typography>
        <Typography
          variant="body02-regular"
          color="neutral.clr-500"
          sx={{ mt: 1, display: "block" }}
        >
          History of all the assessments attempted by you
        </Typography>
      </Box>

      <TextField
        fullWidth
        size="small"
        placeholder="Search assessment..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 18, color: "neutral.clr-400" }} />
            </InputAdornment>
          ),
        }}
      />

      <Box>
        <Typography
          variant="body02-regular"
          color="neutral.clr-500"
          sx={{ mb: 2, display: "block" }}
        >
          Assessment filters
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          {FILTERS.map((f) => {
            const selected = activeFilter === f.field;
            return (
              <Chip
                key={f.field}
                label={f.label}
                onClick={() => toggleFilter(f.field)}
                variant={selected ? "filled" : "outlined"}
                sx={{
                  borderColor: "neutral.clr-200",
                  bgcolor: selected ? "primary.clr-50" : "transparent",
                  color: selected ? "primary.clr-300" : "neutral.clr-600",
                  "& .MuiChip-label": { fontSize: 12 },
                }}
              />
            );
          })}
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {showSkeletons ? (
          Array.from(new Array(SKELETON_COUNT)).map((_, i) => (
            <AttemptCardSkeleton key={i} />
          ))
        ) : pageItems.length ? (
          pageItems.map((attempt) => (
            <AttemptCard
              key={attempt.id}
              attempt={attempt}
              onClick={() => navigateWithClient(`/performance/${attempt.id}`)}
            />
          ))
        ) : allAttempts.length ? (
          // Attempts exist — the search/filter just matched nothing. Desktop
          // only offers "Clear filters" when a filter (not the search) is the
          // cause, since the button doesn't clear the search box.
          <NullState
            icon={AssignmentTurnedInOutlinedIcon}
            description={
              search.trim()
                ? "No assessment found"
                : "No result found for the applied filter"
            }
            onClearFilters={
              search.trim() || !activeFilter
                ? undefined
                : () => setActiveFilter(null)
            }
          />
        ) : (
          <NullState
            icon={AssignmentTurnedInOutlinedIcon}
            description={
              <>
                You have not attempted any assessment so far!
                <br />
                once attempted you can see attempt details here
              </>
            }
          />
        )}
      </Box>

      {!loading && filtered.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
          <Pagination count={pageCount} page={page} onChange={setPage} />
        </Box>
      )}
    </Box>
  );
}
